import React, { useState, useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../../setup/redux/hooks";
import type { RootState } from "../../../../../setup/redux/store";
import { FormWizard } from "src/shared/forms/components/FormWizard";
import { useContentModal } from "src/shared/hooks/useContentModal";
import {
  MerchantProductBrowserModal,
  type MerchantProduct,
} from "src/shared/components";
import type {
  WizardStep,
  WizardCallbacks,
} from "src/shared/forms/types/WizardTypes";
import type { ProductWizardData } from "../../hooks/useProductWizard";
import { fetchAccessibleAccounts } from "../../../account/redux/accountSlice";
import type { Account } from "../../../account/types";
import { clearAccountCategoriesForMarketplace } from "../../../category/redux/accountCategorySlice";
import type { MarketplaceType } from "../../../category/types";
import "src/styles/css/sidebar-layout.css";
import {
  BasicInfoStep,
  CategoryStep,
  SalesInfoStep,
  ShippingStep,
  ReviewStep,
} from "./steps";
import ProductListingService from "../../services/productListingService";
import type { ProductListingDetail } from "../../types/ProductListingDetail";
import type { MarketplaceShipper } from "../../types/ProductListing";
import { transformDetailToWizardData } from "../../utils/dataTransformation";
import { getCategoryIdFromPath } from "../../../category/utils/categoryTreeBuilder";

export interface ProductWizardProps {
  mode?: "create" | "edit" | "view";
  productId?: string;
  onClose: () => void;
  onSubmit?: (data: any) => void | Promise<void>;
}

function ProductWizardNew({
  mode = "create",
  productId,
  onClose,
  onSubmit,
}: ProductWizardProps) {
  const dispatch = useAppDispatch();
  const {
    accounts,
    loading: accountsLoading,
    error: accountsError,
  } = useSelector((state: RootState) => state.Account);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productDetail, setProductDetail] = useState<ProductListingDetail | null>(null);

  // State for available marketplace shippers
  const [availableShippers, setAvailableShippers] = useState<MarketplaceShipper[]>([]);
  const [shippersLoading, setShippersLoading] = useState(false);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const [modalMode, setModalMode] = useState<"link" | "copy">("copy");

  const canShowAnalyticsSidebar = mode === "edit" || mode === "view";
  const [showAnalyticsSidebar, setShowAnalyticsSidebar] = useState(canShowAnalyticsSidebar);
  const [activeMetricsTab, setActiveMetricsTab] = useState<"sales" | "performance" | "ads">("sales");

  const formDataRef = useRef<Record<string, any>>({});
  const [wizardKey, setWizardKey] = useState(0);
  const [prevSelectedStore, setPrevSelectedStore] = useState<string | null>(null);

  // Fetch product details, then available shippers, then transform
  useEffect(() => {
    if (mode === 'edit' && productId) {
      const fetchDetailsAndShippers = async () => {
        setLoading(true);
        setError(null);
        try {
          const service = ProductListingService.getInstance();

          // Step 1: Fetch product details to get marketplace
          const details = await service.getProductListingDetails(productId);
          console.log('=== Product Details Fetched ===');
          console.log('Details.marketplace:', details.marketplace);
          console.log('Details.shippers:', details.shippers);
          setProductDetail(details);

          // Step 2: Fetch available marketplace shippers (non-blocking)
          let fetchedShippers: MarketplaceShipper[] = [];
          try {
            setShippersLoading(true);
            const shippersResponse = await service.getMarketplaceShippers(details.marketplace);
            fetchedShippers = shippersResponse.shipments || [];
            console.log('=== Available Shippers Fetched ===');
            console.log('Available Shippers:', fetchedShippers);
          } catch (shipperError) {
            console.error('[ProductWizard] Failed to fetch marketplace shippers:', shipperError);
            console.warn('[ProductWizard] Continuing with empty shippers array');
            // Continue with empty array - don't break the wizard
          } finally {
            setAvailableShippers(fetchedShippers);
            setShippersLoading(false);
          }

          // Step 3: Transform with available marketplace shippers + enabled product shippers
          const wizardData = transformDetailToWizardData(details, fetchedShippers);
          console.log('=== Wizard Data After Transform ===');
          console.log('wizardData.shipping.shippers:', wizardData.shipping.shippers);
          formDataRef.current = wizardData;
          setWizardKey(Date.now());
        } catch (err) {
          setError('Failed to fetch product details. Please try again.');
          console.error(err);
          setShippersLoading(false);
        } finally {
          setLoading(false);
        }
      };
      fetchDetailsAndShippers();
    }
  }, [mode, productId]);

  const storeOptions = accounts.map((account: Account) => ({
    value: account.id,
    label: `${account.name} - ${account.marketplace.charAt(0).toUpperCase() + account.marketplace.slice(1)}`,
    marketplace: account.marketplace,
  }));

  useEffect(() => {
    dispatch(fetchAccessibleAccounts({ useCache: true }) as any);
  }, [dispatch]);

  useEffect(() => {
    const currentSelectedStore = formDataRef.current?.basicInfo?.selectedStore;
    if (prevSelectedStore && currentSelectedStore && prevSelectedStore !== currentSelectedStore) {
      const prevAccount = accounts.find(acc => acc.id === prevSelectedStore);
      if (prevAccount?.marketplace) {
        dispatch(clearAccountCategoriesForMarketplace(prevAccount.marketplace));
      }
      formDataRef.current = { ...formDataRef.current, category: { ...formDataRef.current.category, initialCategoryPath: [] } };
      setWizardKey(prev => prev + 1);
    }
    if (currentSelectedStore) {
      setPrevSelectedStore(currentSelectedStore);
    }
  }, [prevSelectedStore, accounts, dispatch]);

  const formatCurrency = (amount: number) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount);
  const formatNumber = (num: number) => new Intl.NumberFormat("id-ID").format(num);

  const handleCloseBrowserModal = useCallback(() => setShowBrowserModal(false), []);
  const handleCopyProduct = useCallback((_product: MerchantProduct) => { setShowBrowserModal(false); setModalMode("copy"); }, []);

  const handleLinkProduct = useCallback((product: MerchantProduct) => {
    setShowBrowserModal(false);
    if (mode === "create") localStorage.removeItem("productWizard");
    const currentBasicInfo = formDataRef.current.basicInfo || {};
    formDataRef.current = { ...formDataRef.current, basicInfo: { ...currentBasicInfo, linkedMerchantProduct: product, masterSku: product.sku } };
    setTimeout(() => setWizardKey(prev => prev + 1), 0);
  }, [mode]);

  const handleUnlinkProduct = useCallback(() => {
    if (mode === "create") localStorage.removeItem("productWizard");
    const currentBasicInfo = formDataRef.current.basicInfo || {};
    formDataRef.current = { ...formDataRef.current, basicInfo: { ...currentBasicInfo, linkedMerchantProduct: null, masterSku: "" } };
    setTimeout(() => setWizardKey(prev => prev + 1), 0);
  }, [mode]);

  const handleClose = useCallback(() => {
    const currentSelectedStore = formDataRef.current?.basicInfo?.selectedStore;
    if (currentSelectedStore) {
      const selectedAccount = accounts.find(acc => acc.id === currentSelectedStore);
      if (selectedAccount?.marketplace) dispatch(clearAccountCategoriesForMarketplace(selectedAccount.marketplace as MarketplaceType));
    }
    if (mode === "create") localStorage.removeItem("productWizard");
    onClose();
  }, [accounts, dispatch, mode, onClose]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => { if (event.key === "Escape") handleClose(); };
    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [handleClose]);

  const validateBasicInfo = (data: ProductWizardData["basicInfo"]): string | true => {
    const errors = [];
    if (!data.name) errors.push("Product name is required");
    if (!data.description) errors.push("Product description is required");
    if (!data.selectedStore) errors.push("Store selection is required");
    // SKU validation: Only required in create mode, optional in edit mode
    // In edit mode, user can update empty SKU from input (per user requirement)
    if (mode === "create" && !data.masterSku) errors.push("Master SKU is required");

    // Image validation - only check for images existence
    // Main image validation blocks navigation but doesn't show error (amber warning is sufficient)
    if (!data.images || data.images.length === 0) {
      errors.push("At least one product image is required");  // Strategy 1: Show error
    } else {
      // Validate main image to block navigation, but don't add to errors array
      const mainImages = data.images.filter(img => img.isMain);
      if (mainImages.length === 0) {
        // Strategy 2: Don't show error, just block navigation
        return "Please select a main product image";  // Block navigation but error not shown in array
      } else if (mainImages.length > 1) {
        errors.push("Only one image can be marked as main");  // Edge case
      }
    }

    return errors.length > 0 ? errors.join(". ") : true;  // Return joined string
  };
  const validateCategory = (data: ProductWizardData["category"]): string | true => { const errors = []; const categoryId = getCategoryIdFromPath(data.initialCategoryPath || []); if (!categoryId) errors.push("Category selection is required"); if (!data.brandId) errors.push("Brand selection is required"); return errors.length > 0 ? errors.join(". ") : true; };
  const validateSalesInfo = (data: ProductWizardData["salesInfo"]): string | true => { const errors = []; if (!data.hasVariants) { if (!data.basePrice || data.basePrice <= 0) errors.push("Regular price is required"); if (!data.inventory.quantity || data.inventory.quantity < 0) errors.push("Stock quantity must be specified"); } return errors.length > 0 ? errors.join(". ") : true; };
  const validateShipping = (data: ProductWizardData["shipping"]): string | true => {
    const errors = [];

    // Dimension validation
    if (!data.dimensions.weight || data.dimensions.weight <= 0) errors.push("Package weight is required");
    if (!data.dimensions.length || data.dimensions.length <= 0) errors.push("Package length is required");
    if (!data.dimensions.width || data.dimensions.width <= 0) errors.push("Package width is required");
    if (!data.dimensions.height || data.dimensions.height <= 0) errors.push("Package height is required");

    // Shipper validation - match logic from validation.ts
    // Skip validation if shippers haven't loaded yet
    if (data.shippersLoaded === false) {
      // Still loading - allow navigation
    }

    // If loaded and empty, that's valid (marketplace has no shipping options)
    const shipperCount = Object.keys(data.shippers || {}).length;
    if (data.shippersLoaded && shipperCount === 0) {
      // Marketplace has 0 available shippers - this is acceptable
      // Don't add validation error
    } else if (shipperCount > 0) {
      // Shippers are available - require at least one to be selected
      const hasSelectedShipper = Object.values(data.shippers).some(s => s.enabled);
      if (!hasSelectedShipper) {
        errors.push("At least one shipping shipper must be selected");
      }
    }

    return errors.length > 0 ? errors.join(". ") : true;
  };
  const validateReview = (): true => true;

  const steps: WizardStep[] = [
    { id: "basicInfo", title: "Basic Info", subtitle: "Product details", icon: "inventory_2", component: (props) => <BasicInfoStep {...props} data={props.data as ProductWizardData["basicInfo"]} storeOptions={storeOptions} accountsLoading={accountsLoading} accountsError={accountsError} onLinkProduct={() => { setModalMode("link"); setShowBrowserModal(true); }} onUnlinkProduct={handleUnlinkProduct} />, validation: { validate: validateBasicInfo, required: true } },
    { id: "category", title: "Category", subtitle: "Classification", icon: "category", component: (props) => <CategoryStep {...props} data={props.data as ProductWizardData["category"]} />, validation: { validate: validateCategory, required: true } },
    { id: "salesInfo", title: "Sales Info", subtitle: "Pricing & variants", icon: "sell", component: (props) => <SalesInfoStep {...props} data={props.data as ProductWizardData["salesInfo"]} />, validation: { validate: validateSalesInfo, required: true } },
    { id: "shipping", title: "Shipping", subtitle: "Delivery options", icon: "local_shipping", component: (props) => <ShippingStep {...props} data={props.data as ProductWizardData["shipping"]} />, validation: { validate: validateShipping, required: true } },
    { id: "review", title: "Review", subtitle: "Finalize & publish", icon: "check_circle", component: (props) => <ReviewStep {...props} data={props.allData as ProductWizardData} onEditStep={(stepIndex) => setCurrentStepIndex(stepIndex)} />, validation: { validate: validateReview, required: true } },
  ];

  const initialData: Record<string, any> = formDataRef.current;
  const callbacks: WizardCallbacks = {
    onSubmit: async (data) => { if (onSubmit) await onSubmit(data); handleClose(); },
    onCancel: handleClose,
    onStepChange: setCurrentStepIndex,
    onDataChange: (_stepId, _data, allData) => { formDataRef.current = allData; },
  };

  const { classes } = useContentModal();

  const activeSalesMetrics = productDetail?.salesMetrics;
  const renderMetric = (value: number | undefined, formatter: (num: number) => string | number) => {
    if (loading) return <div className="tw-h-5 tw-w-20 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>;
    return formatter(value ?? 0);
  };
  const renderMetricString = (value: string | undefined) => {
    if (loading) return <div className="tw-h-5 tw-w-24 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>;
    return value || 'N/A';
  }

  const analyticsSidebar = canShowAnalyticsSidebar ? (
    <div className={`analytics-sidebar ${!showAnalyticsSidebar ? 'collapsed' : ''}`}>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        {showAnalyticsSidebar && <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white">Analytics</h3>}
        <button onClick={() => setShowAnalyticsSidebar(!showAnalyticsSidebar)} className="tw-p-2 tw-text-gray-500 hover:tw-text-gray-800 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors">
          <span className="material-symbols-outlined">{showAnalyticsSidebar ? 'chevron_right' : 'analytics'}</span>
        </button>
      </div>
      {showAnalyticsSidebar && (
        <div className="tw-space-y-4">
          <div className="tw-flex tw-border-b tw-border-gray-200 dark:tw-border-gray-600 tw-overflow-x-auto">
            <button onClick={() => setActiveMetricsTab("sales")} className={`tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-border-b-2 tw-transition-colors tw-whitespace-nowrap ${activeMetricsTab === "sales" ? "tw-border-primary tw-text-primary" : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700"}`}>Sales</button>
            <button onClick={() => setActiveMetricsTab("performance")} className={`tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-border-b-2 tw-transition-colors ml-1 ${activeMetricsTab === "performance" ? "tw-border-primary tw-text-primary" : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700"}`}>Performance</button>
            <button onClick={() => setActiveMetricsTab("ads")} className={`tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-border-b-2 tw-transition-colors ml-1 ${activeMetricsTab === "ads" ? "tw-border-primary tw-text-primary" : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700"}`}>Ads</button>
          </div>
          {activeMetricsTab === "sales" && (
            <div className="tw-space-y-4">
              <div className="tw-bg-white dark:tw-bg-gray-800 p-4 rounded-lg border tw-border-gray-200 dark:tw-border-gray-600 shadow-sm">
                <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 mb-3">30-Day Performance</h4>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(activeSalesMetrics?.salesQty30d, formatNumber)}</p><p className="tw-text-xs tw-text-gray-600">Units Sold</p></div>
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(activeSalesMetrics?.salesRevenue30d, formatCurrency)}</p><p className="tw-text-xs tw-text-gray-600">Revenue</p></div>
                  <div><p className="tw-text-sm tw-font-medium">{renderMetric(activeSalesMetrics?.avgDailySalesQty30d, (n) => n.toFixed(1))}</p><p className="tw-text-xs tw-text-gray-500">Avg Daily Units</p></div>
                  <div><p className="tw-text-sm tw-font-medium">{renderMetric(activeSalesMetrics ? activeSalesMetrics.salesRevenue30d / 30 : null, formatCurrency)}</p><p className="tw-text-xs tw-text-gray-500">Avg Daily Revenue</p></div>
                </div>
              </div>
              <div className="tw-bg-white dark:tw-bg-gray-800 p-4 rounded-lg border tw-border-gray-200 dark:tw-border-gray-600 shadow-sm">
                <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 mb-3">100-Day Performance</h4>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-600">Units Sold</p></div>
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, formatCurrency)}</p><p className="tw-text-xs tw-text-gray-600">Revenue</p></div>
                </div>
              </div>
              <div className="tw-bg-white dark:tw-bg-gray-800 p-4 rounded-lg border tw-border-gray-200 dark:tw-border-gray-600 shadow-sm">
                <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 mb-3">Sales Health</h4>
                <div className="tw-space-y-3">
                  <div><p className="tw-text-lg tw-font-bold">{renderMetricString(undefined)}</p><p className="tw-text-xs tw-text-gray-600">Last Order Date</p></div>
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, (n) => `${n.toFixed(1)}x`)}</p><p className="tw-text-xs tw-text-gray-600">Sales Velocity</p></div>
                </div>
              </div>
            </div>
          )}
          {activeMetricsTab === "performance" && (
            <div className="tw-space-y-4">
              <div className="tw-bg-white dark:tw-bg-gray-800 p-4 rounded-lg border tw-border-gray-200 dark:tw-border-gray-600 shadow-sm">
                <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 mb-3">Traffic & Engagement</h4>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-600">Traffic Frequency</p></div>
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-600">Unique Visitors</p></div>
                </div>
              </div>
              <div className="tw-bg-white dark:tw-bg-gray-800 p-4 rounded-lg border tw-border-gray-200 dark:tw-border-gray-600 shadow-sm">
                <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 mb-3">Conversion Metrics</h4>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-600">Add to Cart</p></div>
                  <div><p className="tw-text-lg tw-font-bold">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-600">Orders</p></div>
                  <div><p className="tw-text-sm tw-font-medium">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-500">Unique Buyers</p></div>
                  <div><p className="tw-text-sm tw-font-medium">{renderMetric(undefined, formatNumber)}</p><p className="tw-text-xs tw-text-gray-500">Wishlist Adds</p></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  ) : undefined;

  const renderContent = () => {
    if (error) return <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-p-8 tw-text-red-500">{error}</div>;

    // Show loading indicator for main content only in edit mode while fetching product details
    if (loading && mode === 'edit') {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-p-8">
          <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-primary tw-mb-4"></div>
          <p className="tw-text-gray-500 tw-text-sm">Loading product details...</p>
        </div>
      );
    }

    return <FormWizard key={wizardKey} steps={steps} initialData={initialData} callbacks={callbacks} persistData={mode === "create"} persistKey="productWizard" headless={true} sidebar={analyticsSidebar} showSidebar={canShowAnalyticsSidebar && showAnalyticsSidebar} navigation={{ nextButtonText: currentStepIndex === steps.length - 1 ? (mode === "create" ? "Create Listing" : "Save Changes") : "Next Step", submitButtonText: mode === "create" ? "Create Listing" : "Save Changes", showCancel: true, cancelButtonText: "Cancel" }} />;
  };

  return (
    <>
      <div className={`${classes.container} tw-bg-[#f6f7f8] dark:tw-bg-[#101922] tw-overflow-hidden tw-animate-in tw-fade-in tw-duration-300`} data-testid="wizard-container">
        <header className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-bg-white dark:tw-bg-[#1C252E] tw-px-4 md:tw-px-6 tw-py-4 tw-shadow-sm" data-testid="wizard-header">
          <div className="tw-flex tw-items-center tw-gap-4">
            <button type="button" onClick={handleClose} className="tw-p-2 tw--m-2 tw-text-gray-500 dark:tw-text-gray-400 hover:tw-text-gray-800 dark:hover:tw-text-gray-200 hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-rounded-lg tw-transition-colors" aria-label="Back to product list" data-testid="wizard-header-back-button"><span className="material-symbols-outlined tw-text-xl">arrow_back</span></button>
            <div>
              <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 dark:tw-text-white" data-testid="wizard-header-title">{mode === "create" ? "Create Product Listing" : mode === "edit" ? "Edit Product Listing" : "View Product Details"}</h1>
              <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400" data-testid="wizard-progress-indicator">Step {currentStepIndex + 1} of {steps.length}</p>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            {canShowAnalyticsSidebar && <button onClick={() => setShowAnalyticsSidebar(!showAnalyticsSidebar)} className={`tw-p-2 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors ${showAnalyticsSidebar ? "tw-bg-primary tw-text-white hover:tw-bg-primary/90" : "tw-text-gray-500"}`} title="Toggle Analytics Sidebar" data-testid="wizard-header-analytics-button"><span className="material-symbols-outlined tw-text-lg">analytics</span></button>}
            <button onClick={handleClose} className="tw-p-2 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors" data-testid="wizard-header-close-button"><span className="material-symbols-outlined tw-text-gray-500">close</span></button>
          </div>
        </header>
        <main className="tw-flex-1 tw-overflow-y-auto tw-bg-[#f6f7f8] dark:tw-bg-[#101922]" data-testid="wizard-main-content">{renderContent()}</main>
      </div>
      <MerchantProductBrowserModal isOpen={showBrowserModal} onClose={handleCloseBrowserModal} onSelectProduct={handleLinkProduct} onCopyProduct={handleCopyProduct} mode={modalMode} showBackButton={false} onBack={undefined} title={modalMode === "link" ? "Link Master Product to Merchant Product" : "Copy Merchant Product"} />
    </>
  );
}

export default ProductWizardNew;